import ComposeSendButton from './ComposeSendButton';
import DraftSaveTime, { DraftSaveTimeHandle } from './DraftSaveTime';
import { observer } from 'mobx-react-lite';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { OwaDate, now } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { composeDiscardButton } from 'owa-locstrings/lib/strings/composediscardbutton.locstring.json';
import { composeSendButton } from 'owa-locstrings/lib/strings/composesendbutton.locstring.json';
import setDeferredSendTime from 'owa-mail-compose-actions/lib/actions/setDeferredSendTime';
import setShowSendDiscardAsIcon from 'owa-mail-compose-actions/lib/actions/setShowSendDiscardAsIcon';
import { ComposeViewState, UIEnabledState } from 'owa-mail-compose-store';
import isGroupComposeViewState from 'owa-mail-compose-store/lib/utils/isGroupComposeViewState';
import isPublicFolderComposeViewState from 'owa-mail-compose-store/lib/utils/isPublicFolderComposeViewState';
import { lazyShowScheduleDatePickerDialog } from 'owa-mail-datepicker';
import { getCommands } from 'owa-mail-hotkeys/lib/utils/MailModuleHotKeys';
import { convertHotkeyToReadableText } from 'owa-readable-hotkeys/lib/utils/convertHotkeyToReadableText';
import { getFirstHotkey } from 'owa-readable-hotkeys/lib/utils/getFirstHotkey';
import { hasKeybindForCurrentMode } from 'owa-readable-hotkeys/lib/utils/hasKeybindForCurrentMode';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import * as React from 'react';

const styles = require('./ComposeBottom.scss');
const classNames = require('classnames/bind').bind(styles);

const MIN_WIDTH_FOR_TEXT_BUTTON = 380;

export interface ComposeBottomSendDiscardProps {
    viewState: ComposeViewState;
    composeId: string;
    onSend: () => void;
    onDiscard: () => void;
    children?: React.ReactNode;
}

export interface ComposeBottomSendDiscardHandle {
    resize(): void;
}

/**
 * Renders the send/discard button and the Drafts of the compose bottom.
 */
export default observer(
    function ComposeBottomSendDiscard(
        props: ComposeBottomSendDiscardProps,
        ref: React.Ref<ComposeBottomSendDiscardHandle>
    ) {
        const containerRef = React.useRef<HTMLDivElement>();
        const draftSaveTimeRef = React.useRef<DraftSaveTimeHandle>();
        const renderSendDiscardButton = () => {
            const { viewState, onSend, onDiscard } = props;
            const discardClassName = classNames(styles.discardButton, {
                iconButton: viewState.showSendDiscardAsIcon,
            });
            // The actual displayed title text includes both the action command and the
            // readable hotkey command
            const discardHotkey = getFirstHotkey(getCommands().discardDraft);
            const discardTitle = hasKeybindForCurrentMode(discardHotkey)
                ? `${loc(composeDiscardButton)} (${convertHotkeyToReadableText(discardHotkey)})`
                : loc(composeDiscardButton);
            const sendDisabled = viewState.uiEnabledState !== UIEnabledState.Enabled;
            const isPublicFolderComposeViewStateFlag = isPublicFolderComposeViewState(viewState);
            return (
                <div className={styles.sendDiscard}>
                    <ComposeSendButton
                        onSend={onSend}
                        onScheduledSendClicked={onScheduleSendButton}
                        isPublicFolderComposeViewState={isPublicFolderComposeViewStateFlag}
                        isScheduledSendEnabled={isScheduledSendEnabled(
                            viewState,
                            isPublicFolderComposeViewStateFlag
                        )}
                        isDisabled={sendDisabled}
                        showSendAsIcon={viewState.showSendDiscardAsIcon}
                    />
                    <DefaultButton
                        className={discardClassName}
                        onClick={onDiscard}
                        ariaLabel={loc(composeDiscardButton)}
                        title={discardTitle}>
                        {viewState.showSendDiscardAsIcon ? (
                            <Icon iconName={ControlIcons.Delete} />
                        ) : (
                            loc(composeDiscardButton)
                        )}
                    </DefaultButton>
                </div>
            );
        };
        const onScheduleSend = (date: OwaDate) => {
            setDeferredSendTime(props.viewState, date);
            props.onSend();
        };
        const onScheduleSendButton = () => {
            lazyShowScheduleDatePickerDialog.import().then(showScheduleDatePickerDialog => {
                showScheduleDatePickerDialog({
                    minTime: now(),
                    onScheduleItemClicked: onScheduleSend,
                    acceptButtonText: loc(composeSendButton),
                });
            });
        };
        React.useImperativeHandle(
            ref,
            () => ({
                resize() {
                    if (containerRef.current && draftSaveTimeRef.current) {
                        const { viewState } = props;
                        const availableWidth =
                            containerRef.current.clientWidth - draftSaveTimeRef.current.getWidth();
                        // Calculate showSendDiscardAsIcon with the toolbarWidth and update the viewState if dirty
                        const showSendDiscardAsIcon: boolean =
                            availableWidth < MIN_WIDTH_FOR_TEXT_BUTTON;
                        if (showSendDiscardAsIcon != viewState.showSendDiscardAsIcon) {
                            setShowSendDiscardAsIcon(viewState, showSendDiscardAsIcon);
                        }
                    }
                },
            }),
            []
        );
        return (
            <div ref={containerRef} className={classNames(styles.buttonBar)}>
                {renderSendDiscardButton()}
                {React.Children.count(props.children) == 0
                    ? renderPlaceholderContent()
                    : props.children}
                <DraftSaveTime ref={draftSaveTimeRef} viewState={props.viewState} />
            </div>
        );
    },
    { forwardRef: true }
);

/**
 * When no child is provided, render a placeholder that will grow to the available size
 */
function renderPlaceholderContent() {
    return <div className={styles.buttonBarPlaceholder} />;
}

function isScheduledSendEnabled(
    viewState: ComposeViewState,
    isPublicFolderComposeViewStateFlag: boolean
): boolean {
    return (
        isFeatureEnabled('cmp-scheduledSend') &&
        getUserConfiguration().AsyncSendEnabled &&
        !isGroupComposeViewState(viewState) &&
        !isPublicFolderComposeViewStateFlag
    );
}
